/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Avatar, Link, Skeleton, Typography2 } from '@coursera/cds-core';

import { TooltipWrapper } from 'bundles/common/components/Tooltip';
import type { CourseTypeMetadataFragment } from 'bundles/common/lms-authoring/queries/__generated__/getCourseAndPartnerDataById';
import { useGetEmbeddedSourceCourseQuery } from 'bundles/common/lms-authoring/queries/useGetEmbeddedSourceCourseQuery';

import _t from 'i18n!nls/authoring';

export type SourceCoursePartners = Array<{ name: string; squareLogo?: string; __typename: 'Partner_Partner' }>;

export type Props = {
  embeddedContentSourceCourseId: string;
  partners?: SourceCoursePartners;
  courseName?: string;
  courseSlug?: string;
  includeCourseName?: boolean;
  originalName?: string;
  loading?: boolean;
  boldPartnerName?: boolean;
  className?: string;
};

const styles = {
  attributionContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  logo: css`
    flex: none;
    margin-right: var(--cds-spacing-50);

    img {
      vertical-align: baseline;
    }
  `,
  name: css`
    flex: initial;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  courseNameLink: css`
    color: var(--cds-color-grey-700) !important;
  `,
  loadingLogo: css`
    padding: 3px;

    > * {
      border-radius: 4px;
    }
  `,
};

const isShortFormContent = (courseMetadata?: CourseTypeMetadataFragment) => {
  return courseMetadata?.metadata.__typename === 'Course_ShortFormContentMetadata';
};

export const EmbeddedSourceCourseAttributionSkeleton: React.FC<{ className?: string; includeCourseName?: boolean }> =
  React.memo(({ className, includeCourseName }) => (
    <div data-testid="loading" css={styles.attributionContainer} className={className}>
      <div css={[styles.logo, styles.loadingLogo]}>
        <Skeleton variant="rect" height={24} width={24} />
      </div>
      <Skeleton variant="text" width={includeCourseName ? 300 : 100} css={styles.name} />
    </div>
  ));

const PartnerName: React.FC<{
  partners: SourceCoursePartners;
  boldPartnerName: boolean;
  includeCourseName: boolean;
  shortFormContent: boolean;
  courseName?: string;
  courseSlug?: string | null;
  originalName?: string;
}> = ({ partners, boldPartnerName, includeCourseName, shortFormContent, courseName, courseSlug, originalName }) => {
  const multiplePartners = partners.length > 1;
  const partnerName = multiplePartners ? _t('Multiple educators') : partners[0].name;

  const partnerNameText = boldPartnerName ? (
    <strong>{partnerName}</strong>
  ) : (
    <React.Fragment>{partnerName}</React.Fragment>
  );
  if (!includeCourseName) {
    return partnerNameText;
  }

  if (shortFormContent) {
    return <FormattedMessage message={_t('By {partnerName}')} partnerName={partnerName} />;
  }

  const courseNameLink = courseSlug ? (
    <Link href={`/learn/${courseSlug}`} target="_blank" css={styles.courseNameLink}>
      {courseName}
    </Link>
  ) : (
    courseName
  );

  return (
    <React.Fragment>
      <FormattedMessage
        message={
          multiplePartners
            ? _t('From {courseNameLink} by multiple educators')
            : _t('From {courseNameLink} by {partnerName}')
        }
        courseNameLink={courseNameLink}
        partnerName={partnerName}
      />
      {originalName && _t(", originally titled '#{originalName}'", { originalName })}
    </React.Fragment>
  );
};

const EmbeddedSourceCourseAttribution: React.FC<Props> = ({
  embeddedContentSourceCourseId,
  partners: inputPartners,
  courseName: inputCourseName,
  courseSlug: inputCourseSlug,
  includeCourseName = false,
  originalName,
  loading: loadingOverride = false,
  boldPartnerName = false,
  className,
}) => {
  const { sourceCourse, loading, error } = useGetEmbeddedSourceCourseQuery(
    embeddedContentSourceCourseId,
    !!inputPartners
  );

  if (error) {
    return null;
  }

  if (loading || loadingOverride) {
    return <EmbeddedSourceCourseAttributionSkeleton includeCourseName={includeCourseName} className={className} />;
  }

  const partners = inputPartners ?? sourceCourse?.partners?.filter((partner) => !!partner) ?? [];
  if (partners.length < 1) {
    return null;
  }

  const multiplePartners = partners.length > 1;

  return (
    <div css={styles.attributionContainer} className={`rc-EmbeddedSourceCourseAttribution ${className}`}>
      {partners.map((partner) => (
        <TooltipWrapper
          show={multiplePartners}
          key={partner.squareLogo}
          placement="bottom"
          delayShow={100}
          tooltipId={'SourcePartnerInfo' + partner.name}
          message={partner.name}
        >
          <div>
            <Avatar
              key={partner.squareLogo}
              variant="organization"
              size={24}
              showBorder={true}
              imgProps={{
                src: partner.squareLogo || undefined,
              }}
              aria-hidden
              data-testid="partner-logo"
              css={styles.logo}
            />
          </div>
        </TooltipWrapper>
      ))}
      <Typography2
        variant="bodySecondary"
        component="span"
        color="supportText"
        data-testid="embedded-text-content"
        css={styles.name}
      >
        <PartnerName
          partners={partners}
          boldPartnerName={boldPartnerName}
          includeCourseName={includeCourseName}
          shortFormContent={isShortFormContent(sourceCourse?.courseTypeMetadata)}
          courseName={inputCourseName || sourceCourse?.name}
          courseSlug={inputCourseSlug || sourceCourse?.slug}
          originalName={originalName}
        />
      </Typography2>
    </div>
  );
};

export default EmbeddedSourceCourseAttribution;
